import React, { useState } from "react";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import { Plus, Trash2, X } from "lucide-react";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import { useCreateAccount } from "../hooks/useAccounts";
import { useMainStore } from "@/stores/index";
import { showError } from "@/shared/utils/toast";

import { startAccountResearchJobsToast } from "@/features/campaigns/utils/enrollmentBatchToast";

const MAX_ACCOUNT_LINKS = 10;

const normalizeWebsiteUrl = (value) => {
  try {
    const url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
    return ["http:", "https:"].includes(url.protocol) && url.hostname
      ? url.toString()
      : null;
  } catch {
    return null;
  }
};

const AddAccountModal = ({ isOpen, onClose }) => {
  const { user } = useMainStore();
  const [accountUrls, setAccountUrls] = useState([""]);

  const { mutate: createAccount, isPending: isCreatingAccount } =
    useCreateAccount({
      action: (data) => {
        startAccountResearchJobsToast(data.map((item) => item?.job_id));
        handleClose();
      },
    });

  const handleCreateAccount = () => {
    const enteredUrls = accountUrls.map((url) => url.trim()).filter(Boolean);
    const websiteUrls = enteredUrls.map(normalizeWebsiteUrl);

    if (!websiteUrls.length) return;
    if (websiteUrls.some((url) => !url)) {
      showError("Please enter a valid website link for each account.");
      return;
    }
    const accountDomains = websiteUrls.map((url) =>
      new URL(url).hostname.replace(/^www\./i, "").toLowerCase(),
    );
    if (new Set(accountDomains).size !== accountDomains.length) {
      showError("Please remove duplicate account links.");
      return;
    }

    createAccount({ userId: user?.id, websiteUrls });
  };

  const updateAccountUrl = (index, value) => {
    setAccountUrls((urls) =>
      urls.map((url, urlIndex) => (urlIndex === index ? value : url)),
    );
  };

  const addAccountUrl = () => {
    setAccountUrls((urls) =>
      urls.length < MAX_ACCOUNT_LINKS ? [...urls, ""] : urls,
    );
  };

  const removeAccountUrl = (index) => {
    setAccountUrls((urls) => urls.filter((_, urlIndex) => urlIndex !== index));
  };

  const handleClose = () => {
    setAccountUrls([""]);
    onClose();
  };

  const hasAccountUrl = accountUrls.some((url) => url.trim());
  const accountCount = accountUrls.filter((url) => url.trim()).length;

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleClose}
      width="500px"
      className="p-6 rounded-2xl"
    >
      <div className="flex w-full flex-col gap-5">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-medium text-gray-800 dark:text-gray-100">Add Account</h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label
              htmlFor="account-url-0"
              className="text-xs font-medium text-gray-700 dark:text-gray-300"
            >
              Company website links
            </label>
            <span className="text-[0.7rem] text-gray-500 dark:text-gray-400">
              {accountUrls.length}/{MAX_ACCOUNT_LINKS}
            </span>
          </div>
          <div className="flex max-h-64 flex-col gap-2 overflow-y-auto pr-1">
            {accountUrls.map((accountUrl, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  id={`account-url-${index}`}
                  type="url"
                  value={accountUrl}
                  onChange={(event) =>
                    updateAccountUrl(index, event.target.value)
                  }
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") return;
                    event.preventDefault();
                    if (accountUrls.length < MAX_ACCOUNT_LINKS) addAccountUrl();
                    else handleCreateAccount();
                  }}
                  placeholder="https://example.com"
                  autoFocus={index === 0}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none focus:border-secondary"
                />
                {accountUrls.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeAccountUrl(index)}
                    aria-label={`Remove account link ${index + 1}`}
                    className="rounded-md p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-error-500"
                  >
                    <Trash2 size={16} />
                  </button>
                ) : null}
              </div>
            ))}
          </div>
          {accountUrls.length < MAX_ACCOUNT_LINKS ? (
            <button
              type="button"
              onClick={addAccountUrl}
              className="flex w-fit items-center gap-1 text-xs font-medium text-secondary hover:opacity-80"
            >
              <Plus size={14} />
              Add another link
            </button>
          ) : null}
          <p className="text-[0.7rem] text-gray-500 dark:text-gray-400">
            Add up to 10 websites. We’ll research each one and generate its
            account profile.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <SecondaryButton
            title="Cancel"
            onClick={handleClose}
          />
          <PrimaryButton
            startContent={false}
            disabled={!hasAccountUrl}
            isLoading={isCreatingAccount}
            onClick={handleCreateAccount}
          >
            Generate {accountCount || ""} Account{accountCount === 1 ? "" : "s"}
          </PrimaryButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default AddAccountModal;
